import type { Entrypoint, PageType } from 'types';
import * as uuid from 'uuid';

import { log } from '~/utils/log';
import { deriveSourceMedium } from '~/utils/sourceMedium';

export const SEARCH_ENGINES = [
  'baidu',
  'bing',
  'duckduckgo',
  'ecosia',
  'google',
  'qwant',
  'yahoo',
  'yandex'
];

const STORAGE_KEY = 'pageContext';

export const PLP_SEGMENTS = ['release-dates', 'sole-market', 'clothing'];

const SOCIAL_MEDIUMS = ['social', 'social-paid', 'social-organic'];

const SEARCH_MEDIUMS = ['cpc', 'organic', 'paid-search', 'ppc'];

const SOCIAL_SOURCES = [
  'facebook',
  'instagram',
  'linkedin',
  'pinterest',
  'reddit',
  'snapchat',
  'tiktok',
  'twitter',
  'youtube'
];

type StoredState = {
  currentContext: unknown;
  entry_point?: Entrypoint;
  pageSeq?: number;
  pageType: PageType;
  pageViewId?: string;
  path?: string;
  previousContext: unknown;
  previousPagePath?: string;
  previousPagePosition?: string;
  previousPageType?: PageType;
  previousPageViewId?: string;
  source_medium?: string;
};

const DEFAULT_STATE: StoredState = {
  currentContext: null,
  entry_point: undefined,
  pageSeq: 0,
  pageType: 'unknown',
  pageViewId: undefined,
  path: undefined,
  previousContext: null,
  previousPagePath: undefined,
  previousPagePosition: undefined,
  previousPageType: undefined,
  previousPageViewId: undefined,
  source_medium: undefined
};

function load(): StoredState {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (raw) return { ...DEFAULT_STATE, ...JSON.parse(raw) };
  } catch {
    // sessionStorage may be unavailable
  }

  return { ...DEFAULT_STATE };
}

function save(state: StoredState) {
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch {
    // sessionStorage may be unavailable
  }
}

function isSearchEngineReferrer(referrer: string): boolean {
  try {
    const hostname = new URL(referrer).hostname.toLowerCase();

    return SEARCH_ENGINES.some((engine) => hostname.includes(engine));
  } catch {
    return false;
  }
}

function deriveEntrypoint(current: Entrypoint | undefined): Entrypoint {
  if (current) return current;

  const params = new URLSearchParams(window.location.search);
  const source = params.get('utm_source')?.toLowerCase() ?? '';
  const medium = params.get('utm_medium')?.toLowerCase() ?? '';

  if (medium === 'email') return 'email';

  if (SOCIAL_SOURCES.includes(source) || SOCIAL_MEDIUMS.includes(medium)) {
    return 'social';
  }

  if (SEARCH_MEDIUMS.includes(medium)) return 'search';

  if (isSearchEngineReferrer(document.referrer)) return 'search';

  return 'unknown';
}

function derivePageTypeFromUrl(url: string | undefined): PageType | undefined {
  if (!url) return;

  const [path, query] = url.split('?');
  const segments = path!.split('/').filter(Boolean);

  if (segments.length === 0) return 'homepage';
  if (segments[0] === 'news' || segments[0] === 'features') return 'article';
  if (segments[0] === 'sale') return 'sale';
  if (segments[0] === 's') return 'pdp';

  const plpIndex = segments.findIndex((s) => PLP_SEGMENTS.includes(s));

  if (plpIndex !== -1) {
    const segmentsAfterKey = segments.length - plpIndex - 1;

    if (segmentsAfterKey >= 3) return 'pdp';

    const params = new URLSearchParams(query ?? '');

    return params.has('q') ? 'search' : 'plp';
  }

  return 'unknown';
}

export function setNavigationContext<T = unknown>(context: T) {
  const state = load();

  state.currentContext = context;
  save(state);
}

export function getCurrentContext<T = unknown>(): T | null {
  return (load().currentContext as T) ?? null;
}

export function getPreviousContext<P = unknown>(): P | null {
  return (load().previousContext as P) ?? null;
}

export function getEntrypoint(): Entrypoint {
  return load().entry_point ?? 'unknown';
}

export function getPageType(): PageType {
  return load().pageType ?? 'unknown';
}

export function getPageSeq(): number {
  return load().pageSeq ?? 0;
}

export function getPageViewId(): string | undefined {
  return load().pageViewId;
}

export function getPreviousPagePath(): string | undefined {
  return load().previousPagePath;
}

export function getSourceAndMedium(): string | undefined {
  return load().source_medium;
}

export function getPreviousPageType(): PageType | undefined {
  const state = load();
  const ctx = state.previousContext as { previousPageType?: PageType } | null;

  return ctx?.previousPageType ?? state.previousPageType;
}

export function getPreviousPagePosition(): string | undefined {
  const ctx = getPreviousContext<{ pagePosition?: string }>();

  return ctx?.pagePosition;
}

export function getPreviousPageViewId(): string | undefined {
  return load().previousPageViewId;
}

let advancedSinceLoad = false;

export function advanceNavigationContext(path: string) {
  if (typeof document === 'undefined') return;

  const prevState = load();
  const sameUrlRerender = advancedSinceLoad && path === prevState.path;

  advancedSinceLoad = true;

  if (sameUrlRerender) {
    log.info('advanceNavigationContext', load());

    return;
  }

  const nextState: StoredState = {
    ...prevState,
    currentContext: null,
    entry_point: deriveEntrypoint(prevState.entry_point),
    pageSeq: (prevState.pageSeq ?? 0) + 1,
    pageType: derivePageTypeFromUrl(path) ?? 'unknown',
    pageViewId: uuid.v4(),
    path,
    previousContext: prevState.currentContext,
    previousPagePath: prevState.path?.split(/[?#]/)[0],
    previousPagePosition: getPreviousPagePosition(),
    previousPageType: derivePageTypeFromUrl(prevState.path),
    previousPageViewId: prevState.pageViewId,
    source_medium:
      prevState.source_medium ||
      deriveSourceMedium(
        window.location.search,
        document.referrer,
        window.location.hostname
      )
  };

  save(nextState);
  log.info('Current Navigation Context:', nextState);
}
